import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { STAGES } from './stages';
import { CalendarDays, Printer } from 'lucide-react';

const stageLabel = (key) => { const s = STAGES.find((x) => x.key === key); return s ? s.label : key; };

const Group = ({ label, jobs, onOpenJob }) => {
  const targetDate = jobs.length ? jobs[0].target_dispatch : null;
  return (
    <Card data-testid={`dispatch-group-${label.split(' ')[0].toLowerCase()}`}>
      <CardContent className="p-0">
        <div className="px-4 py-3 border-b bg-secondary/60 flex items-center justify-between rounded-t">
          <div className="flex items-center gap-2 font-medium"><CalendarDays size={16} className="text-accent" />{label}{targetDate ? <span className="text-sm text-muted-foreground">· {targetDate}</span> : ''}</div>
          <span className="text-xs bg-white/60 rounded-full px-2 py-0.5">{jobs.length} job(s)</span>
        </div>
        {jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No jobs in this batch</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/40"><tr>
                <th className="px-4 py-2 text-left text-xs font-medium">Job</th>
                <th className="px-4 py-2 text-left text-xs font-medium">Customer</th>
                <th className="px-4 py-2 text-left text-xs font-medium">Units</th>
                <th className="px-4 py-2 text-left text-xs font-medium">Current Stage</th>
                <th className="px-4 py-2 text-left text-xs font-medium">Status</th>
              </tr></thead>
              <tbody className="divide-y">
                {jobs.map((j) => (
                  <tr key={j.id} className="hover:bg-muted/30 cursor-pointer" onClick={() => onOpenJob(j.id)} data-testid={`dispatch-row-${j.id}`}>
                    <td className="px-4 py-2 font-mono text-sm">{j.job_number}</td>
                    <td className="px-4 py-2 text-sm">{j.customer}</td>
                    <td className="px-4 py-2 text-sm">{j.units}</td>
                    <td className="px-4 py-2 text-sm">
                      {j.status === 'completed'
                        ? <span className="text-green-600">Ready to dispatch</span>
                        : <span className="text-amber-600">{stageLabel(j.current_stage)}</span>}
                    </td>
                    <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${j.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{j.status === 'completed' ? 'Completed' : 'In Progress'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DispatchSheet = ({ jobs, onOpenJob }) => {
  const wednesday = jobs.filter((j) => j.dispatch_day === 'wednesday');
  const saturday = jobs.filter((j) => j.dispatch_day === 'saturday');
  const unscheduled = jobs.filter((j) => j.dispatch_day !== 'wednesday' && j.dispatch_day !== 'saturday');

  const printSheet = () => {
    const esc = (v) => String(v == null ? '' : v).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const section = (title, list) => {
      if (!list.length) return '';
      const date = list[0].target_dispatch || '';
      const rows = list.map((j) => `<tr><td class="chk">☐</td><td>${esc(j.job_number)}</td><td>${esc(j.customer)}</td><td>${esc(j.units)}</td><td>${j.status === 'completed' ? 'Ready to dispatch' : esc(stageLabel(j.current_stage))}</td></tr>`).join('');
      return `<h2>${esc(title)} ${date ? '· ' + esc(date) : ''}</h2>
        <table><thead><tr><th class="chk">Loaded</th><th>Job</th><th>Customer</th><th>Units</th><th>Stage / Status</th></tr></thead><tbody>${rows}</tbody></table>`;
    };
    const html = `<!doctype html><html><head><title>Dispatch Sheet</title><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#111}
      h1{font-size:20px;margin:0 0 4px} .sub{color:#666;font-size:12px;margin-bottom:16px}
      h2{font-size:15px;margin:20px 0 8px;border-bottom:2px solid #FF6600;padding-bottom:4px}
      table{width:100%;border-collapse:collapse;margin-bottom:8px}
      th,td{border:1px solid #ccc;padding:6px 8px;font-size:12px;text-align:left}
      th{background:#f3f3f3} .chk{width:52px;text-align:center;font-size:16px}
      @media print{.noprint{display:none}}
    </style></head><body>
      <h1>Factory Dispatch Sheet</h1>
      <div class="sub">Generated ${new Date().toLocaleString('en-IN')}</div>
      ${section('Wednesday Dispatch', wednesday)}
      ${section('Saturday Dispatch', saturday)}
      ${unscheduled.length ? section('Unscheduled', unscheduled) : ''}
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="space-y-4" data-testid="dispatch-sheet">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={printSheet} data-testid="print-dispatch-btn"><Printer size={14} className="mr-1" />Print Loading Sheet</Button>
      </div>
      <Group label="Wednesday Dispatch" jobs={wednesday} onOpenJob={onOpenJob} />
      <Group label="Saturday Dispatch" jobs={saturday} onOpenJob={onOpenJob} />
      {unscheduled.length > 0 && <Group label="Unscheduled" jobs={unscheduled} onOpenJob={onOpenJob} />}
    </div>
  );
};

export default DispatchSheet;
